/**
 * linkifyRichText — turns URLs, #hashtags and @mentions inside
 * already-sanitized post/comment HTML into clickable elements.
 *
 * Runs after `sanitizeHtml`, never before: the sanitizer strips `button`
 * elements and unknown attributes, so the markup added here would not
 * survive it. Only text nodes are rewritten, so existing links, embeds and
 * formatting are left untouched.
 */

import type { MentionIndex } from '@wlb/shared/utils/mentions';
import { parseRichText } from './parseRichText';

/** Elements whose text must stay plain (already interactive or verbatim). */
const SKIPPED_ANCESTORS = new Set(['A', 'CODE', 'PRE', 'IFRAME']);

export const HASHTAG_ATTR = 'data-hashtag';
export const MENTION_ATTR = 'data-mention';
/** Identity resolved while linkifying; clicks read this, never the label. */
export const MENTION_ID_ATTR = 'data-mention-id';
/** Present when the label belongs to more than one known member. */
export const MENTION_AMBIGUOUS_ATTR = 'data-mention-ambiguous';

const hasSkippedAncestor = (node: Node, root: Node): boolean => {
  let current = node.parentNode;
  while (current && current !== root) {
    if (current.nodeType === Node.ELEMENT_NODE
      && SKIPPED_ANCESTORS.has((current as Element).tagName)) {
      return true;
    }
    current = current.parentNode;
  }
  return false;
};

const buildLink = (
  doc: Document,
  attribute: string,
  value: string,
  label: string,
  className: string,
): HTMLElement => {
  const span = doc.createElement('span');
  span.setAttribute(attribute, value);
  span.setAttribute('role', 'button');
  span.setAttribute('tabindex', '0');
  span.className = className;
  span.textContent = label;
  return span;
};

const buildUrlLink = (doc: Document, value: string): HTMLAnchorElement => {
  const link = doc.createElement('a');
  link.href = /^www\./i.test(value) ? `https://${value}` : value;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.className = 'text-link';
  link.textContent = value;
  return link;
};

/** Replaces one text node with a mix of text and interactive links. */
const linkifyTextNode = (
  doc: Document,
  textNode: Text,
  mentions?: MentionIndex,
): void => {
  const text = textNode.textContent ?? '';
  const segments = parseRichText(text, mentions);
  if (segments.length === 1 && segments[0].type === 'text') return;

  const fragment = doc.createDocumentFragment();
  segments.forEach(segment => {
    if (segment.type === 'text') {
      fragment.appendChild(doc.createTextNode(segment.value));
    } else if (segment.type === 'url') {
      fragment.appendChild(buildUrlLink(doc, segment.value));
    } else if (segment.type === 'mention') {
      const identity = segment.match;
      // Only resolved, non-ambiguous mentions are interactive; others stay
      // as plain text so they never navigate to the wrong (or no) profile.
      if (identity.userGuid && !identity.ambiguous) {
        const displayLabel = identity.displayName
          ? `@${identity.displayName}`
          : `@${segment.value}`;
        const span = buildLink(doc, MENTION_ATTR, segment.value, displayLabel, 'mention-link');
        span.setAttribute(MENTION_ID_ATTR, identity.userGuid);
        fragment.appendChild(span);
      } else {
        const span = doc.createElement('span');
        span.className = 'mention-label';
        span.textContent = `@${segment.value}`;
        fragment.appendChild(span);
      }
    } else {
      fragment.appendChild(
        buildLink(doc, HASHTAG_ATTR, segment.value.slice(1), segment.value, 'hashtag-link'),
      );
    }
  });
  textNode.parentNode?.replaceChild(fragment, textNode);
};

/**
 * Returns the HTML with URLs, hashtags and mentions wrapped in clickable
 * elements.
 * `HtmlContent` handles the clicks by delegation using the data attributes.
 */
export function linkifyRichText(
  sanitizedHtml: string,
  mentions?: MentionIndex,
): string {
  if (!sanitizedHtml) return '';
  if (!/(?:https?:\/\/|www\.|[@#])/i.test(sanitizedHtml)) return sanitizedHtml;

  const doc = new DOMParser().parseFromString(sanitizedHtml, 'text/html');
  const body = doc.body;
  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);

  const targets: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    if (!hasSkippedAncestor(node, body)) targets.push(node as Text);
    node = walker.nextNode();
  }

  targets.forEach(textNode => linkifyTextNode(doc, textNode, mentions));
  return body.innerHTML;
}
